import { useEffect, useRef } from "react";
import type * as mapboxgl from "mapbox-gl";
import type { WindField } from "../../types/weatherGrid";
import { sampleWindField } from "../../utils/weatherGrid";

interface WindParticleLayerProps {
  map: mapboxgl.Map | null;
  isActive: boolean;
  /** Uniform wind (km/h, met. degrees FROM) — used wherever `field` is absent. */
  windSpeed: number;
  windDirection: number;
  /**
   * Optional per-point wind lattice for the current hour. When present each
   * particle is advected by the bilinearly interpolated speed/direction at
   * its own position (so trails bend across the project); the scalar props
   * are then only a fallback for a missing/invalid field.
   */
  field?: WindField | null;
  bounds: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

interface Particle {
  lng: number;
  lat: number;
  age: number;
  maxAge: number;
  trail: Array<{ lng: number; lat: number }>;
  /** Local wind speed (km/h) at the particle's last update; drives colour/opacity. */
  speed: number;
}

interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// ---------------------------------------------------------------------------
// Tuning
//
// Everything visual is defined in *screen* terms so the layer reads the same
// at any zoom and any wind: a particle always moves a perceptible number of
// pixels per second, trails always span a legible length, and even calm
// winds keep a solid opacity floor.  Speed only modulates these on top.
// ---------------------------------------------------------------------------

/** Particles per 1 Mpx of visible project area (clamped below). */
const PARTICLE_DENSITY_PER_MPX = 260;
const MIN_PARTICLES = 140;
const MAX_PARTICLES = 700;

/** Screen speed: px/s at 0 km/h, plus px/s per km/h. 10 km/h → ~50 px/s. */
const BASE_PX_PER_SEC = 28;
const PX_PER_SEC_PER_KMH = 2.2;
const MAX_PX_PER_SEC = 190;

/** Trail length in *pixels* (interpolated by speed ratio 0..1). */
const MIN_TRAIL_PX = 26;
const MAX_TRAIL_PX = 110;

/** Opacity floor/ceiling by speed ratio; age fade applies on top. */
const MIN_OPACITY = 0.62;
const MAX_OPACITY = 1.0;

const MIN_PARTICLE_AGE_MS = 1800;
const MAX_PARTICLE_AGE_MS = 4200;
const FADE_FRACTION = 0.15;

/** Speed (km/h) that saturates the colour / length / opacity ramps. */
const SPEED_SATURATION_KMH = 60;

const HEAD_RADIUS_PX = 1.9;
const LINE_WIDTH_PX = 1.8;
const HALO_EXTRA_PX = 2.2;
const HALO_ALPHA = 0.55;

const FRAME_INTERVAL_MS = 1000 / 30;

/**
 * Colour by speed (km/h).  Chosen for contrast on both satellite/dark and
 * light basemaps; the dark halo drawn beneath each trail carries the rest.
 */
const getWindSpeedColor = (speed: number): string => {
  if (speed < 5) return "205, 232, 255"; // near-white blue
  if (speed < 10) return "140, 215, 255"; // sky
  if (speed < 20) return "90, 255, 205"; // mint
  if (speed < 30) return "255, 228, 90"; // yellow
  if (speed < 40) return "255, 160, 60"; // orange
  return "255, 90, 90"; // red
};

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

const WindParticleLayer = ({
  map,
  isActive,
  windSpeed,
  windDirection,
  field = null,
  bounds,
}: WindParticleLayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const windSpeedRef = useRef(windSpeed);
  const windDirectionRef = useRef(windDirection);
  const fieldRef = useRef<WindField | null>(field);
  const boundsRef = useRef(bounds);

  // Update refs when props change
  useEffect(() => {
    windSpeedRef.current = windSpeed;
    windDirectionRef.current = windDirection;
    fieldRef.current = field;
    boundsRef.current = bounds;
  }, [windSpeed, windDirection, field, bounds]);

  // Wind at a position: the interpolated lattice when a field is attached,
  // otherwise the uniform scalar pair.
  const windAt = (lng: number, lat: number): { speed: number; direction: number } => {
    const f = fieldRef.current;
    if (f && f.lats.length > 0 && f.lons.length > 0) {
      return sampleWindField(f, lng, lat);
    }
    return { speed: windSpeedRef.current, direction: windDirectionRef.current };
  };

  useEffect(() => {
    if (!map || !isActive) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (canvasRef.current && canvasRef.current.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
        canvasRef.current = null;
      }
      return;
    }

    // If the canvas already exists the refs above carry the new wind params.
    if (canvasRef.current) return;

    // ---- Geometry helpers -------------------------------------------------

    /** Project bounds ∩ current viewport (in lng/lat); null when disjoint. */
    const spawnBox = (): Box | null => {
      const b = boundsRef.current;
      const view = map.getBounds();
      if (!view) return b;
      const box = {
        top: Math.min(b.top, view.getNorth()),
        bottom: Math.max(b.bottom, view.getSouth()),
        left: Math.max(b.left, view.getWest()),
        right: Math.min(b.right, view.getEast()),
      };
      if (box.top <= box.bottom || box.right <= box.left) return null;
      return box;
    };

    /** Degrees of lng/lat per screen pixel at the viewport centre. */
    const degreesPerPixel = (): { lng: number; lat: number } => {
      const c = map.getContainer();
      const cx = c.clientWidth / 2;
      const cy = c.clientHeight / 2;
      const p0 = map.unproject([cx, cy]);
      const p1 = map.unproject([cx + 1, cy + 1]);
      return {
        lng: Math.abs(p1.lng - p0.lng) || 1e-9,
        lat: Math.abs(p0.lat - p1.lat) || 1e-9,
      };
    };

    /** Visible project area in screen pixels → particle budget. */
    const targetParticleCount = (box: Box | null): number => {
      if (!box) return MIN_PARTICLES;
      const tl = map.project([box.left, box.top]);
      const br = map.project([box.right, box.bottom]);
      const areaMpx = (Math.abs(br.x - tl.x) * Math.abs(br.y - tl.y)) / 1e6;
      return Math.round(
        Math.min(MAX_PARTICLES, Math.max(MIN_PARTICLES, areaMpx * PARTICLE_DENSITY_PER_MPX)),
      );
    };

    // ---- Particles --------------------------------------------------------

    const createParticle = (box: Box | null): Particle => {
      const b = box ?? boundsRef.current;
      const lng = b.left + Math.random() * (b.right - b.left);
      const lat = b.bottom + Math.random() * (b.top - b.bottom);
      return {
        lng,
        lat,
        age: 0,
        maxAge: MIN_PARTICLE_AGE_MS + Math.random() * (MAX_PARTICLE_AGE_MS - MIN_PARTICLE_AGE_MS),
        trail: [{ lng, lat }],
        speed: windAt(lng, lat).speed,
      };
    };

    const respawn = (p: Particle, box: Box | null, staggerAge: boolean) => {
      const fresh = createParticle(box);
      p.lng = fresh.lng;
      p.lat = fresh.lat;
      p.age = staggerAge ? Math.random() * fresh.maxAge * 0.6 : 0;
      p.maxAge = fresh.maxAge;
      p.trail = fresh.trail;
      p.speed = fresh.speed;
    };

    const syncParticleCount = (box: Box | null) => {
      const want = targetParticleCount(box);
      const list = particlesRef.current;
      while (list.length < want) {
        const p = createParticle(box);
        // Stagger ages so a freshly (re)filled swarm doesn't fade in together.
        p.age = Math.random() * p.maxAge * 0.6;
        list.push(p);
      }
      if (list.length > want) list.length = want;
    };

    const updateParticle = (
      particle: Particle,
      deltaMs: number,
      dpp: { lng: number; lat: number },
      box: Box | null,
    ) => {
      const { speed, direction } = windAt(particle.lng, particle.lat);
      particle.speed = speed;
      const b = boundsRef.current;

      // Met. "from" bearing → movement bearing (clockwise from north).
      const radians = ((direction + 180) % 360) * (Math.PI / 180);

      // Screen-space speed, converted to degrees at the current zoom, so the
      // motion is always perceptible regardless of project size or zoom.
      const pxPerSec = Math.min(MAX_PX_PER_SEC, BASE_PX_PER_SEC + speed * PX_PER_SEC_PER_KMH);
      const stepPx = (pxPerSec * deltaMs) / 1000;
      const dx = Math.sin(radians) * stepPx; // east
      const dy = Math.cos(radians) * stepPx; // north

      // Trail length in pixels → number of retained points at this step size.
      const speedRatio = clamp01(speed / SPEED_SATURATION_KMH);
      const trailPx = MIN_TRAIL_PX + (MAX_TRAIL_PX - MIN_TRAIL_PX) * speedRatio;
      const maxPoints = Math.max(4, Math.round(trailPx / Math.max(stepPx, 0.25)));

      particle.trail.push({ lng: particle.lng, lat: particle.lat });
      while (particle.trail.length > maxPoints) particle.trail.shift();

      particle.lng += dx * dpp.lng;
      particle.lat += dy * dpp.lat;
      particle.age += deltaMs;

      // Respawn when leaving the project, leaving the visible spawn box, or
      // dying of old age.  Leaving the viewport respawns inside it so the
      // density stays even wherever the user is looking.
      const out =
        particle.lng < b.left ||
        particle.lng > b.right ||
        particle.lat < b.bottom ||
        particle.lat > b.top ||
        (box !== null &&
          (particle.lng < box.left - (box.right - box.left) * 0.1 ||
            particle.lng > box.right + (box.right - box.left) * 0.1 ||
            particle.lat < box.bottom - (box.top - box.bottom) * 0.1 ||
            particle.lat > box.top + (box.top - box.bottom) * 0.1));
      if (out || particle.age > particle.maxAge) {
        respawn(particle, box, false);
      }
    };

    // ---- Drawing ----------------------------------------------------------

    const drawParticles = () => {
      if (!map || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      // Project every trail once; skip particles fully off-screen.
      for (const particle of particlesRef.current) {
        if (particle.trail.length < 2) continue;
        const pts: Array<{ x: number; y: number }> = [];
        let onScreen = false;
        for (const t of particle.trail) {
          const p = map.project([t.lng, t.lat]);
          pts.push(p);
          if (p.x >= -20 && p.x <= width + 20 && p.y >= -20 && p.y <= height + 20) onScreen = true;
        }
        const head = map.project([particle.lng, particle.lat]);
        pts.push(head);
        if (!onScreen) continue;

        const speedRatio = clamp01(particle.speed / SPEED_SATURATION_KMH);
        let opacity = MIN_OPACITY + (MAX_OPACITY - MIN_OPACITY) * speedRatio;
        const t = particle.age / particle.maxAge;
        if (t < FADE_FRACTION) opacity *= t / FADE_FRACTION;
        else if (t > 1 - FADE_FRACTION) opacity *= (1 - t) / FADE_FRACTION;
        if (opacity <= 0.01) continue;

        const colour = getWindSpeedColor(particle.speed);

        // Halo: a dark, slightly wider stroke underneath so the trail reads on
        // light *and* dark basemaps.
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.strokeStyle = `rgba(0, 0, 0, ${HALO_ALPHA * opacity})`;
        ctx.lineWidth = LINE_WIDTH_PX + HALO_EXTRA_PX;
        ctx.stroke();

        // Trail: tapered alpha from tail to head via a gradient along the path.
        const tail = pts[0];
        const grad = ctx.createLinearGradient(tail.x, tail.y, head.x, head.y);
        grad.addColorStop(0, `rgba(${colour}, 0)`);
        grad.addColorStop(1, `rgba(${colour}, ${opacity})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = LINE_WIDTH_PX;
        ctx.stroke();

        // Head.
        ctx.beginPath();
        ctx.arc(head.x, head.y, HEAD_RADIUS_PX + 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 0, 0, ${HALO_ALPHA * opacity})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(head.x, head.y, HEAD_RADIUS_PX, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${colour}, ${opacity})`;
        ctx.fill();
      }
    };

    // ---- Canvas overlay ---------------------------------------------------

    const mapCanvas = map.getCanvas();
    const mapContainer = mapCanvas.parentElement;
    if (!mapContainer) {
      console.error("WindParticleLayer: No map container found");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.style.position = "absolute";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "2";

    const sizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const cssW = mapCanvas.clientWidth;
      const cssH = mapCanvas.clientHeight;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    };
    sizeCanvas();
    mapContainer.appendChild(canvas);
    canvasRef.current = canvas;

    const handleResize = () => {
      if (canvasRef.current) sizeCanvas();
    };
    map.on("resize", handleResize);

    // ---- Animation loop ---------------------------------------------------

    let lastTime = performance.now();
    let lastBoxKey = "";

    const animate = () => {
      const now = performance.now();
      const deltaMs = now - lastTime;
      if (deltaMs < FRAME_INTERVAL_MS) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      lastTime = now;

      const box = spawnBox();
      const boxKey = box ? `${box.top.toFixed(4)}|${box.bottom.toFixed(4)}|${box.left.toFixed(4)}|${box.right.toFixed(4)}` : "none";
      if (boxKey !== lastBoxKey) {
        lastBoxKey = boxKey;
        syncParticleCount(box);
      }

      const dpp = degreesPerPixel();
      // Cap the step so a background-tab hiccup doesn't fling particles.
      const step = Math.min(deltaMs, 3 * FRAME_INTERVAL_MS);
      for (const p of particlesRef.current) updateParticle(p, step, dpp, box);

      drawParticles();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    particlesRef.current = [];
    syncParticleCount(spawnBox());
    animate();

    // Redraw immediately on pan/zoom so trails don't lag the basemap.
    const handleMapUpdate = () => drawParticles();
    map.on("move", handleMapUpdate);
    map.on("zoom", handleMapUpdate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      map.off("resize", handleResize);
      map.off("move", handleMapUpdate);
      map.off("zoom", handleMapUpdate);
      if (canvasRef.current && canvasRef.current.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
        canvasRef.current = null;
      }
    };
  }, [map, isActive]); // Only depend on map and isActive, not wind params

  return null;
};

export default WindParticleLayer;
